import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import {
  Map, MapPin, CalendarRange, UsersRound, Ticket, Clock, CheckCircle2, IndianRupee,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const COLORS = ["#C85A32", "#2B4C3F", "#D4AF37", "#3B6E8F", "#B5527E"];

function StatCard({ icon: Icon, label, value, to, accent }) {
  const inner = (
    <div className="rounded-xl border border-border bg-card p-5 hover:-translate-y-0.5 hover:shadow-md transition-transform duration-200" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <Icon className={`h-5 w-5 ${accent || "text-primary"}`} />
      </div>
      <p className="font-heading text-4xl mt-2">{value}</p>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/analytics").then(({ data }) => setAnalytics(data));
  }, []);

  if (!stats) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-4xl font-semibold">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your Safarshala operations.</p>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Map} label="Total Trips" value={stats.total_trips} to="/admin/trips" />
        <StatCard icon={MapPin} label="Active Trips" value={stats.active_trips} to="/admin/trips" accent="text-emerald-600 dark:text-emerald-400" />
        <StatCard icon={CalendarRange} label="Upcoming Departures" value={stats.upcoming_departures} to="/admin/departures" />
        <StatCard icon={UsersRound} label="Total Leads" value={stats.total_leads} to="/admin/leads" accent="text-emerald-600 dark:text-emerald-400" />
        <StatCard icon={Ticket} label="Total Bookings" value={stats.total_bookings} to="/admin/bookings" />
        <StatCard icon={Clock} label="Pending Payments" value={stats.pending_payments} to="/admin/bookings" accent="text-amber-600 dark:text-amber-400" />
        <StatCard icon={CheckCircle2} label="Confirmed" value={stats.confirmed_bookings} to="/admin/bookings" accent="text-emerald-600 dark:text-emerald-400" />
        <StatCard icon={IndianRupee} label="Collected (verified invoices)" value={inr(stats.advance_revenue)} accent="text-primary" />
      </div>

      {analytics && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border bg-card p-5">
            <h3 className="font-heading text-2xl mb-4">Leads by Source</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={analytics.leads_by_source} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {analytics.leads_by_source.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-xl border bg-card p-5">
            <h3 className="font-heading text-2xl mb-4">Leads by Trip</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics.leads_by_trip} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" hide /><YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                <Tooltip /><Bar dataKey="value" fill="#C85A32" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
